<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Draggable from 'vuedraggable';
import { useRoute } from 'vue-router';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AddAttribute from './AddAttribute.vue';
import EditAttribute from './EditAttribute.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import AttributeListItem from 'dashboard/components-next/ConversationWorkflow/AttributeListItem.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFieldOrder } from 'dashboard/composables/useFieldOrder';
import { SOURCE_ATTRIBUTE_KEYS } from 'dashboard/components-next/Forms/sourceAttributeKeys';

const { t } = useI18n();

const getters = useStoreGetters();
const store = useStore();
const route = useRoute();
const { currentAccount } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const [showAddPopup, toggleAddPopup] = useToggle(false);
const [showEditPopup, toggleEditPopup] = useToggle(false);
const [showDeletePopup, toggleDeletePopup] = useToggle(false);
const selectedAttribute = ref({});
const searchQuery = ref('');
const uiFlags = computed(() => getters['attributes/getUIFlags'].value);

const attributeModels = [
  'conversation_attribute',
  'contact_attribute',
  'company_attribute',
  'deal_attribute',
  'product_attribute',
];

const tabIndexFromQuery = () => {
  const tab = route.query?.tab;
  const idx = attributeModels.indexOf(tab);
  return idx >= 0 ? idx : 0;
};

const selectedTabIndex = ref(tabIndexFromQuery());

const fieldOrders = {
  contact_attribute: useFieldOrder('contact'),
  company_attribute: useFieldOrder('company'),
  deal_attribute: useFieldOrder('deal'),
  product_attribute: useFieldOrder('product'),
};

const openAddPopup = () => {
  toggleAddPopup(true);
};
const hideAddPopup = () => {
  toggleAddPopup(false);
};
const hideEditPopup = () => {
  toggleEditPopup(false);
  selectedAttribute.value = {};
};
const closeDelete = () => {
  toggleDeletePopup(false);
  selectedAttribute.value = {};
};

const tabs = computed(() => [
  { key: 0, name: t('ATTRIBUTES_MGMT.TABS.CONVERSATION') },
  { key: 1, name: t('ATTRIBUTES_MGMT.TABS.CONTACT') },
  { key: 2, name: t('ATTRIBUTES_MGMT.TABS.COMPANY') },
  { key: 3, name: t('ATTRIBUTES_MGMT.TABS.DEAL') },
  { key: 4, name: t('ATTRIBUTES_MGMT.TABS.PRODUCT') },
]);

const tabsForTabBar = computed(() =>
  tabs.value.map(tab => ({ label: tab.name, key: tab.key }))
);

onMounted(() => {
  store.dispatch('attributes/get');
});

const attributeModel = computed(
  () => attributeModels[selectedTabIndex.value] || 'conversation_attribute'
);

// Esta lista mostra só os campos personalizados; os padrão seguem nos formulários.
// Na reordenação eles continuam na ordem salva (ver o `fullOrder` abaixo).
const isHiddenField = field => field.type === 'standard';

const attributes = computed(() =>
  getters['attributes/getAttributesByModel']
    .value(attributeModel.value)
    .filter(
      attribute => !SOURCE_ATTRIBUTE_KEYS.includes(attribute.attribute_key)
    )
);

const onClickTabChange = tab => {
  selectedTabIndex.value = tab.key;
  searchQuery.value = '';
};

// Na lista ordenável o `id` do item é o de ordenação (`custom:<chave>`); a API
// precisa do registro real, então ele é resolvido pela chave do campo.
const findStoredAttribute = attribute =>
  attributes.value.find(
    item => item.attribute_key === attribute.attribute_key
  ) || attribute;

const handleEditAttribute = attribute => {
  selectedAttribute.value = findStoredAttribute(attribute);
  toggleEditPopup(true);
};

const handleDeleteAttribute = attribute => {
  selectedAttribute.value = findStoredAttribute(attribute);
  toggleDeletePopup(true);
};

const confirmDeleteAttribute = async () => {
  try {
    await store.dispatch('attributes/delete', selectedAttribute.value.id);
    useAlert(t('ATTRIBUTES_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    closeDelete();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('ATTRIBUTES_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const requiredAttributeKeys = computed(
  () => currentAccount.value?.settings?.conversation_required_attributes || []
);

const hasPreChatBadge = attribute => {
  return (inboxes.value || []).some(inbox => {
    const fields =
      inbox?.pre_chat_form_options?.pre_chat_fields ||
      inbox?.channel?.pre_chat_form_options?.pre_chat_fields ||
      [];
    return fields.some(field => field.name === attribute.attribute_key);
  });
};

const buildBadges = attribute => {
  const badges = [];
  if (hasPreChatBadge(attribute)) {
    badges.push({
      type: 'pre-chat',
    });
  }

  if (
    attribute.attribute_model === 'conversation_attribute' &&
    requiredAttributeKeys.value.includes(attribute.attribute_key)
  ) {
    badges.push({
      type: 'resolution',
    });
  }

  return badges;
};

const derivedAttributes = computed(() =>
  attributes.value.map(attribute => ({
    ...attribute,
    label: attribute.attribute_display_name,
    type: t('ATTRIBUTES_MGMT.CUSTOM_BADGE'),
    value: attribute.attribute_key,
    badges: buildBadges(attribute),
  }))
);

const fieldOrder = computed(() => fieldOrders[attributeModel.value]);
const isReorderable = computed(() => !!fieldOrder.value);

// Contato, empresa e negociação listam os campos personalizados na ordem salva
// na conta; a aba de conversa lista na ordem de criação.
const listItems = computed(() => {
  if (!fieldOrder.value) return derivedAttributes.value;
  return fieldOrder.value.orderedFields.value
    .filter(field => !isHiddenField(field))
    .map(field => {
      const match = derivedAttributes.value.find(
        item => item.value === field.key
      );
      return match ? { ...match, id: field.id } : null;
    })
    .filter(Boolean);
});

const displayItems = ref([]);
watch(
  listItems,
  items => {
    displayItems.value = items;
  },
  { immediate: true }
);

const filteredAttributes = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return displayItems.value;
  return picoSearch(displayItems.value, query, [
    'attribute_display_name',
    'attribute_key',
    'attribute_description',
  ]);
});

const onReorder = async items => {
  displayItems.value = items;
  try {
    // Hidden fields stay where they are; only the visible slots get reordered.
    const queue = items.map(item => item.id);
    const fullOrder = fieldOrder.value.orderedFields.value.map(field =>
      isHiddenField(field) ? field.id : queue.shift()
    );
    await fieldOrder.value.saveOrder(fullOrder);
  } catch {
    displayItems.value = listItems.value;
    useAlert(t('ATTRIBUTES_MGMT.ORDER.SAVE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('ATTRIBUTES_MGMT.HEADER')"
        :description="$t('ATTRIBUTES_MGMT.DESCRIPTION')"
        :link-text="$t('ATTRIBUTES_MGMT.LEARN_MORE')"
        :search-placeholder="$t('ATTRIBUTES_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="custom_attributes"
      >
        <template v-if="listItems.length" #count>
          <span class="text-body-main text-n-slate-11 truncate min-w-0">
            {{ $t('ATTRIBUTES_MGMT.COUNT', { n: listItems.length }) }}
          </span>
        </template>
        <template #tabs>
          <TabBar
            :tabs="tabsForTabBar"
            :initial-active-tab="selectedTabIndex"
            @tab-changed="onClickTabChange"
          />
        </template>
        <template #actions>
          <Button
            :label="$t('ATTRIBUTES_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex flex-col gap-4">
        <span
          v-if="!filteredAttributes.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('ATTRIBUTES_MGMT.NO_RESULTS') }}
        </span>
        <Draggable
          v-else-if="filteredAttributes.length"
          :model-value="filteredAttributes"
          item-key="id"
          tag="div"
          handle=".drag-handle"
          :disabled="!isReorderable || !!searchQuery.trim()"
          class="flex flex-col divide-y divide-n-weak border-t border-n-weak"
          @update:model-value="onReorder"
        >
          <template #item="{ element: attribute }">
            <AttributeListItem
              :attribute="attribute"
              :badges="attribute.badges"
              :reorderable="isReorderable && !searchQuery.trim()"
              @edit="handleEditAttribute"
              @delete="handleDeleteAttribute"
            />
          </template>
        </Draggable>
        <p
          v-else
          class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
        >
          {{ $t('ATTRIBUTES_MGMT.LIST.EMPTY_RESULT.404') }}
        </p>
      </div>
    </template>
    <AddAttribute
      v-if="showAddPopup"
      v-model:show="showAddPopup"
      :on-close="hideAddPopup"
      :selected-attribute-model-tab="selectedTabIndex"
    />
    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAttribute
        :selected-attribute="selectedAttribute"
        :is-updating="uiFlags.isUpdating"
        @on-close="hideEditPopup"
      />
    </woot-modal>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.TITLE', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      :message="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="`${$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.YES')} ${
        selectedAttribute.attribute_display_name || ''
      }`"
      :reject-text="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.NO')"
      :confirm-value="selectedAttribute.attribute_display_name"
      :confirm-place-holder-text="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      @on-confirm="confirmDeleteAttribute"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
